import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit2, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import Loader from '../../components/ui/Loader';
import Badge from '../../components/ui/Badge';
import opcionService from '../../services/opcionService';
import preguntaService from '../../services/preguntaService';

export default function Opciones() {
  const [loading, setLoading] = useState(true);
  const [opciones, setOpciones] = useState([]);
  const [preguntas, setPreguntas] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Cargar preguntas primero
      let preguntasData = [];
      try {
        preguntasData = await preguntaService.getAllPreguntas();
        setPreguntas(preguntasData || []);
      } catch (preguntaError) {
        console.error('Error cargando preguntas:', preguntaError);
        if (preguntaError.response?.status !== 400) {
          toast.error('Error al cargar preguntas');
        }
        setPreguntas([]);
      }

      // Cargar opciones
      let opcionesData = [];
      try {
        opcionesData = await opcionService.getAllOpciones();
        setOpciones(opcionesData || []);
      } catch (opcionError) {
        console.error('Error cargando opciones:', opcionError);
        if (opcionError.response?.status !== 400) {
          toast.error('Error al cargar opciones');
        }
        setOpciones([]);
      }
    } catch (error) {
      console.error('Error general:', error);
      setOpciones([]);
      setPreguntas([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    try {
      const payload = {
        texto: data.texto,
        esCorrecta: data.esCorrecta === 'true' || data.esCorrecta === true,
        preguntaId: parseInt(data.preguntaId)
      };

      if (editingItem) {
        await opcionService.updateOpcion(editingItem.id, payload);
        toast.success('Opción actualizada');
      } else {
        await opcionService.createOpcion(payload);
        toast.success('Opción creada');
      }
      loadData();
      closeModal();
    } catch (error) {
      toast.error('Error al guardar');
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar esta opción?')) return;
    
    try {
      await opcionService.deleteOpcion(id);
      toast.success('Opción eliminada');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
      console.error(error);
    }
  };

  const openModal = (item = null) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const filteredOpciones = opciones.filter(o =>
    o.texto?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <Loader />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-neutral-800">Opciones de Respuesta</h1>
          <p className="text-neutral-600 mt-1">Gestión de opciones para preguntas</p>
        </div>
        <Button onClick={() => openModal()} className="gap-2">
          <Plus className="w-4 h-4" />
          Nueva Opción
        </Button>
      </div>

      <div className="flex justify-between items-center gap-4">
        <div className="flex-1 max-w-md">
          <Input
            placeholder="Buscar opciones..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={Search}
          />
        </div>
        <Button variant="outline" onClick={loadData} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Texto</th>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Pregunta</th>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Estado</th>
              <th className="text-right p-4 text-sm font-semibold text-neutral-700">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200">
            {filteredOpciones.length === 0 ? (
              <tr>
                <td colSpan="4" className="p-8 text-center text-neutral-500">
                  No se encontraron opciones
                </td>
              </tr>
            ) : (
              filteredOpciones.map((opcion) => (
                <tr key={opcion.id} className="hover:bg-neutral-50">
                  <td className="p-4 font-medium text-neutral-800 max-w-md">
                    {opcion.texto}
                  </td>
                  <td className="p-4 text-neutral-600 max-w-xs truncate">
                    {opcion.preguntaTexto || opcion.pregunta?.texto || 'N/A'}
                  </td>
                  <td className="p-4">
                    {opcion.esCorrecta ? (
                      <Badge variant="success">Correcta</Badge>
                    ) : (
                      <Badge variant="secondary">Incorrecta</Badge>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => openModal(opcion)}
                        className="p-2 text-primary hover:bg-primary-50 rounded-md transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(opcion.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <OpcionModal
        isOpen={showModal}
        onClose={closeModal}
        onSave={handleSave}
        initialData={editingItem}
        preguntas={preguntas}
      />
    </div>
  );
}

function OpcionModal({ isOpen, onClose, onSave, initialData, preguntas }) {
  const [formData, setFormData] = useState({
    texto: '',
    esCorrecta: false,
    preguntaId: ''
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        texto: initialData.texto || '',
        esCorrecta: initialData.esCorrecta || false,
        preguntaId: initialData.preguntaId || initialData.pregunta?.id || ''
      });
    } else {
      setFormData({
        texto: '',
        esCorrecta: false,
        preguntaId: ''
      });
    }
  }, [initialData, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialData ? 'Editar Opción' : 'Nueva Opción'}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Texto de la opción
          </label>
          <textarea
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
            rows={3}
            value={formData.texto}
            onChange={(e) => setFormData({ ...formData, texto: e.target.value })}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Pregunta
          </label>
          <select
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
            value={formData.preguntaId}
            onChange={(e) => setFormData({ ...formData, preguntaId: e.target.value })}
            required
          >
            <option value="">Seleccionar pregunta</option>
            {preguntas.map(p => (
              <option key={p.id} value={p.id}>{p.texto}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.esCorrecta}
              onChange={(e) => setFormData({ ...formData, esCorrecta: e.target.checked })}
              className="w-4 h-4 text-primary border-neutral-300 rounded focus:ring-primary"
            />
            <span className="text-sm font-medium text-neutral-700">
              ¿Es la respuesta correcta?
            </span>
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit">
            Guardar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
